import axios from "axios";
import { ApiURI } from "../types/configs";
import { ReportState, ReportType } from "../types/Rows";
import { headers } from "./headers";

export const StatisticsInstance = axios.create({
  baseURL: ApiURI + "Statistics/",
});

// ── DTOs ────────────────────────────────────────────────────────────────────
// Wszystkie dane bez badań testowych/szablonów (jak przypomnienia e-mail).

export interface VisitWithoutReport {
  visitId: string;
  visitName: string;
  studyName: string;
  resortName: string;
  visitDate: string;
  daysSince: number;
  monitors: string[];
}

export interface PendingReport {
  reportId: string;
  reportName: string;
  studyName: string;
  resortName: string;
  author: string;
  state: ReportState;
  reportType: ReportType;
  since: string;
  daysSince: number;
}

export interface ToDo {
  visitsWithoutReport: VisitWithoutReport[];
  draftReports: PendingReport[];
  reviewQueue: PendingReport[];
}

export interface OpenItem {
  kind: "finding" | "action";
  reportId: string;
  category: string;
  description: string;
  participantNumber: string;
  identificationDate: string | null;
  daysOpen: number | null;
  major: boolean;
  potentialDeviation: boolean;
  seriousBreach: boolean;
}

export interface ResortOverview {
  resortId: string;
  resortName: string;
  studyId: string;
  studyName: string;
  reportCount: number;
  lastVisitDate: string | null;
  nextPlannedVisitDate: string | null;
  latestReportId: string | null;
  openMajorFindings: number;
  openMinorFindings: number;
  openActions: number;
  overdueOpenItems: number;
  openPotentialDeviations: number;
  seriousBreaches: number;
  openItems: OpenItem[];
}

export interface SiteStatus {
  declared: number;
  afterScreening: number;
  screenFailure: number;
  randomized: number;
  duringTreatment: number;
  duringFollowUp: number;
  prematureCompletion: number;
  completedStudy: number;
  reportedSAE: number;
  protocolDeviations: number;
}

export interface ResortStatus {
  resortId: string;
  resortName: string;
  reportId: string;
  reportName: string;
  asOf: string;
  status: SiteStatus;
}

export interface StudyStatus {
  studyId: string;
  studyName: string;
  resortCount: number;
  resortsWithData: number;
  oldestAsOf: string | null;
  status: SiteStatus;
  resorts: ResortStatus[];
}

export interface TurnaroundRow {
  studyId: string;
  studyName: string;
  visits: number;
  submitted: number;
  accepted: number;
  returnedForCorrection: number;
  medianDaysToSubmit: number | null;
  medianDaysToAccept: number | null;
  medianDaysInReview: number | null;
}

export interface Turnaround {
  total: TurnaroundRow;
  byStudy: TurnaroundRow[];
}

// ── API calls ────────────────────────────────────────────────────────────────

export const fetchToDo = async (): Promise<ToDo> =>
  (await StatisticsInstance.get<ToDo>("to-do", headers())).data;

export const fetchResortsOverview = async (): Promise<ResortOverview[]> =>
  (await StatisticsInstance.get<ResortOverview[]>("resorts", headers())).data;

export const fetchStudyStatus = async (): Promise<StudyStatus[]> =>
  (await StatisticsInstance.get<StudyStatus[]>("studies", headers())).data;

export const fetchTurnaround = async (months: number): Promise<Turnaround> =>
  (await StatisticsInstance.get<Turnaround>("turnaround", { ...headers(), params: { months } })).data;
